import { Card, Col, Heading1, Heading4, Margin, P, Row } from '@ovotech/nebula';
import { useAccountContext } from '@ovotech/ui-tools';
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Anytime,
  GreenerEnergyOfferOneClick,
  HomeHeatOffer,
  OffersLoading,
  SmartCharger,
  SmartMeterBooking,
} from './components';

import { AnnualBoilerService } from './components/cards/AnnualBoilerService';
import { useContractsQueryLegacy } from '@/src/api/use-contracts-query';
import { useProfileQueryKapi } from '@/src/api/kapi/use-profile-query';

import {
  AnalyticsWrapper,
  DisplayFlex,
  FootnoteCommon,
  FootnoteProvider,
  FootnoteYield,
  Section,
} from '@/src/components';

import { VIEWED_OFFERS_SCREEN } from '@/src/constants/analytics';

import {
  ALLOW_ANYTIME_SIGNUP,
  HOME_RECOVER_OFFER,
  HOME_SERVICES_BLOCK_EXISTING_CUSTOMERS,
  HOMEPLAN_OFFER,
  OHS_2026_OFFER_CARDS,
  SHOW_ABS_IN_MORE_OFFERS,
  SHOW_GREENER_ELECTRICITY,
} from '@/src/constants/feature-flags';
import { PRODUCT_IDS } from '@/src/constants/products';
import { OFFERS_AND_UPGRADES_SECTION_ID } from '@/src/constants/sectionIds';
import { getProductCatalogueStart } from '@/src/redux/ducks/products/products';
import { getDateRangeMonthlyUsageStart } from '@/src/redux/ducks/usage/monthly-usage';
import { State } from '@/src/types/State';

import { hasActiveContract } from '@/src/utils/contracts';
import {
  OptimizelyContext,
  OptimizelyFeature,
  useFeature,
} from '@/src/utils/optimizely';
import {
  getEligibleProductFromCatalogue,
  getZeroCarbonHeatingOffer,
} from '@/src/utils/products';
import BeyondBanner from '@/src/pages/Offers/components/BeyondBanner';
import { useBeyondAccountStatusQuery } from '@/src/api/use-beyond-account-status';
import { BeyondAccountStatus } from '@/src/types/Response';
import { GreenerElectricityOffer } from './components/cards/GreenerElectricityOffer';
import { bottomBeyondBannerTerms } from './components/FooterTermsAndConditions';
import { HomeRecoverPromoOffer } from './components/cards/homeplan/HomeRecoverPromoOffer';
import { HomePlanPromoOffer } from './components/cards/homeplan/HomePlanPromoOffer';
import { useProductsQuery } from '@/src/api';
import { boltons } from '@monovo/ev-anytime';
import { useCustomerClassificationV2 } from '@/src/hooks/HomeServices/useCustomerClassificationV2';
import { FreeEssentials } from './components/cards/homeplan/FreeEssentials';
import { EPD } from './components/cards/homeplan/EPD';

const corgiPromos = ['homePlanPromo', 'homerecover', 'homeheat'];

export const OffersAndUpgrades = () => {
  const { accountId } = useAccountContext();
  const { data: beyondAccountStatusData } = useBeyondAccountStatusQuery();
  const { optimizely } = useContext(OptimizelyContext);
  const dispatch = useDispatch();
  const products = useProductsQuery(accountId, { staleTime: 10000 });

  const catalogue = useSelector((state: State) => state.products.catalogue);
  const smartMeter = useSelector(
    (state: State) =>
      state.app.gasSupply?.supplyPointInfo?.isSmart ||
      state.app.elecSupply?.supplyPointInfo?.isSmart,
  );

  const [showGreenerElectricity] = useFeature(SHOW_GREENER_ELECTRICITY);
  const [homeplanOffer] = useFeature(HOMEPLAN_OFFER);
  const [homeplan2026Cards] = useFeature(OHS_2026_OFFER_CARDS);
  const [blockExistingHomeServicesCustomers] = useFeature(
    HOME_SERVICES_BLOCK_EXISTING_CUSTOMERS,
  );

  const {
    hasBoilerInsurance,
    hasAbs,
    hasEpd,
    isReady: isClassificationReady,
  } = useCustomerClassificationV2();

  const profile = useProfileQueryKapi();
  const contracts = useContractsQueryLegacy(accountId);

  const currentYear = new Date(Date.now()).getFullYear();
  const years = [currentYear, currentYear - 1];

  const offers: React.ReactNode[] = [];

  const offerPromos = [];

  useEffect(() => {
    dispatch(getProductCatalogueStart());
    dispatch(getDateRangeMonthlyUsageStart(years.map(year => year.toString())));
    // TODO: See https://github.com/ovotech/orion-ui/issues/2861
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getProductCatalogueStart, getDateRangeMonthlyUsageStart]);

  const isLoading = !catalogue.data || contracts.isLoading || profile.isLoading;

  if (isLoading) {
    return <OffersLoading />;
  }

  const isPostCodeSouthEast = getZeroCarbonHeatingOffer(
    profile?.data?.customer_nextV1.contactAddress?.postalAddress.postalCode ??
      '',
  );

  const hasGreenerEnergyOffer =
    catalogue.data &&
    !!getEligibleProductFromCatalogue(
      catalogue.data,
      PRODUCT_IDS.greenerEnergy,
    );

  const hasGreenerElectricityOffer =
    showGreenerElectricity &&
    catalogue.data &&
    !!getEligibleProductFromCatalogue(
      catalogue.data,
      PRODUCT_IDS.greenerElectricity,
    );

  const hasChargeAnytime =
    products.data &&
    (boltons.findMonthlyBolton(products.data.boltons) ||
      boltons.findPaydBolton(products.data.boltons));

  if (hasGreenerEnergyOffer) {
    offers.push(<GreenerEnergyOfferOneClick />);
  }

  if (hasGreenerElectricityOffer) {
    offers.push(<GreenerElectricityOffer />);
  }

  const hasGas = contracts.data && hasActiveContract(contracts.data.gas);

  // MEC-based filtering (CGI-40178): when enabled, only show boiler offers to
  // customers we've confirmed don't already hold the relevant product. Boiler
  // Cover and Home Recover are hidden if the customer holds any boiler cover
  // product (incl. legacy BC); ABS is additionally hidden if they hold ABS.
  const canOfferBoilerCover =
    !blockExistingHomeServicesCustomers ||
    (isClassificationReady && !hasBoilerInsurance);
  const canOfferAbs =
    !blockExistingHomeServicesCustomers ||
    (isClassificationReady && !hasBoilerInsurance && !hasAbs);

  const canOfferEpd =
    homeplan2026Cards &&
    isClassificationReady &&
    !hasEpd &&
    !hasBoilerInsurance;

  if (hasGas && accountId) {
    if (homeplanOffer && canOfferBoilerCover) {
      if (homeplan2026Cards && !hasAbs) {
        offers.push(<FreeEssentials />);
      } else {
        offers.push(<HomePlanPromoOffer accountId={accountId} />);
      }
      offerPromos.push('homePlanPromo');
    }
  }

  if (
    optimizely?.isFeatureEnabled(SHOW_ABS_IN_MORE_OFFERS) &&
    accountId &&
    canOfferAbs
  ) {
    offers.push(<AnnualBoilerService accountId={accountId} />);
  }

  if (canOfferEpd) {
    offers.push(<EPD />);
  }

  if (
    optimizely &&
    optimizely.isFeatureEnabled(HOME_RECOVER_OFFER) &&
    accountId &&
    canOfferBoilerCover
  ) {
    offers.push(<HomeRecoverPromoOffer accountId={accountId} />);
    offerPromos.push('homerecover');
  }

  if (hasGas) {
    offers.push(<HomeHeatOffer />);
    offerPromos.push('homeheat');
  }

  if (isPostCodeSouthEast) {
    offerPromos.push('zeroCarbonHeating');
  }

  offers.push(<SmartCharger />);
  offerPromos.push('smartCharger');

  if (!smartMeter) {
    offers.push(<SmartMeterBooking />);
    offerPromos.push('smartMeterBooking');
  }

  if (!hasChargeAnytime) {
    offers.push(
      <OptimizelyFeature feature={ALLOW_ANYTIME_SIGNUP}>
        {isEnabled => (isEnabled ? <Anytime /> : null)}
      </OptimizelyFeature>,
    );
    offerPromos.push('ovoDriveAnytime');
  }

  const footnoteContent = [
    <>
      OVO Energy Ltd is registered in England and Wales, company number
      06890795. Registered office: Floor 5, Crescent, Temple Back, Redcliffe,
      BS1 6EZ.
      <br></br>
    </>,
  ];

  if (offerPromos.some(promo => corgiPromos.includes(promo))) {
    footnoteContent.unshift(
      <>
        OVO Energy Ltd is an appointed representative of CORGI HomePlan (for
        general insurance distribution) which is authorised and regulated by the
        Financial Conduct Authority under firm reference number 824122
      </>,
      <>
        OVO Home Services Ltd and OVO Energy Ltd are part of the OVO Group Ltd.
      </>,
    );
  }

  const isIneligible =
    beyondAccountStatusData?.beyondAccountStatus.status ===
    BeyondAccountStatus.Ineligible;

  const showBeyondBanner =
    beyondAccountStatusData?.beyondAccountStatus.status && !isIneligible;

  const beyondBannerContent = {
    top: {
      heading: 'Unlock our best offers with Beyond.',
      subHeading: 'Only via the OVO app',
      ctaText: 'Learn more',
    },
    bottom: {
      heading: 'Get exclusive discounts and ways to save up to £1000.',
      subHeading: 'With Beyond, via the OVO app',
      ctaText: 'Start saving',
      footnote: bottomBeyondBannerTerms,
    },
  };

  return (
    <FootnoteProvider markerType="number">
      <Section id={OFFERS_AND_UPGRADES_SECTION_ID}>
        <Margin bottom={showBeyondBanner ? 4 : 6}>
          <Card>
            <Heading1>Offers &amp; upgrades</Heading1>
          </Card>
        </Margin>

        {showBeyondBanner && <BeyondBanner {...beyondBannerContent.top} />}

        {accountId ? (
          <>
            {offers.length ? (
              <>
                <AnalyticsWrapper
                  name={VIEWED_OFFERS_SCREEN}
                  properties={{ offerPromos }}
                >
                  <Row isNested data-testid="offers-row">
                    {offers.map((offer, index) => (
                      <Col medium={6} key={`offer-${index}`} as={DisplayFlex}>
                        {offer}
                      </Col>
                    ))}
                  </Row>
                </AnalyticsWrapper>
                {showBeyondBanner && (
                  <BeyondBanner {...beyondBannerContent.bottom} />
                )}
                <FootnoteCommon content={footnoteContent} />
                <FootnoteYield
                  heading={<Heading4>Important Information</Heading4>}
                />
              </>
            ) : (
              <Card>
                <P>
                  We don't have any offers available at the moment, please check
                  back later!
                </P>
              </Card>
            )}
          </>
        ) : (
          <OffersLoading />
        )}
      </Section>
    </FootnoteProvider>
  );
};
